import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import AvatarGroup from '@mui/material/AvatarGroup';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import { DataGrid } from '@mui/x-data-grid/DataGrid';

import { formatBoolean, getPrefColor, getPrefIcon } from '../../../utils/formatters';
import { dashboardAPI } from '../../../utils/dashboardAPI.js';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../AuthContext';

const NotificationPreferencesTable = ({ autoRefresh }) => {
    const { token } = useAuth();

    // preferences change rarely: poll every 5min when autoRefresh enabled
    const { data: preferencesData, isFetching } = useQuery({
        queryKey: [ 'preferences' ],
        queryFn: () => dashboardAPI.getPreferences(token),
        staleTime: 300000,
        retry: 1,
        refetchInterval: autoRefresh ? 300000 : false
    });

    const preferences = Array.isArray(preferencesData) ? preferencesData : [];
    const columns = [
        {
            field: 'name',
            headerName: 'Patroller',
            width: 150,
            valueFormatter: (value, row) => value || row.id || 'Unknown'
        },
        {
            field: 'smsConsent',
            headerName: 'SMS Consent',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => formatBoolean(value)
        },
        {
            field: 'phoneVerified',
            headerName: 'Phone Verified',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => formatBoolean(value)
        },
        {
            field: 'emailVerified',
            headerName: 'Email Verified',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (value) => formatBoolean(value)
        },
        {
            field: 'emailNotifications',
            headerName: 'Email',
            width: 200,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => (
                <AvatarGroup max={ params.value.length } sx={{ justifyContent: 'flex-start', display: 'flex' }}>
                    { params.value.map((idx) => (
                        <Tooltip key={ idx } title={ idx }>
                            <Avatar
                                size="small"
                                sx={ {
                                    bgcolor: getPrefColor(idx),
                                    width: 32,
                                    height: 32
                                } }
                            >
                                { getPrefIcon(idx) }
                            </Avatar>
                        </Tooltip>
                    )) }
                </AvatarGroup>
            )
        },
        {
            field: 'smsNotifications',
            headerName: 'SMS',
            width: 200,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => (
                <AvatarGroup max={ params.value.length } sx={{ justifyContent: 'flex-start', display: 'flex' }}>
                    { params.value.map((idx) => (
                        <Tooltip key={ idx } title={ idx }>
                            <Avatar
                                size="small"
                                sx={ {
                                    bgcolor: getPrefColor(idx),
                                    width: 32,
                                    height: 32
                                } }
                            >
                                { getPrefIcon(idx) }
                            </Avatar>
                        </Tooltip>
                    )) }
                </AvatarGroup>
            )
        },
        {
            field: 'voiceNotifications',
            headerName: 'Voice',
            width: 200,
            align: 'left',
            headerAlign: 'left',
            renderCell: (params) => (
                <AvatarGroup max={ params.value.length } sx={{ justifyContent: 'flex-start', display: 'flex' }}>
                    { params.value.map((idx) => (
                        <Tooltip key={ idx } title={ idx }>
                            <Avatar
                                size="small"
                                sx={ {
                                    bgcolor: getPrefColor(idx),
                                    width: 32,
                                    height: 32
                                } }
                            >
                                { getPrefIcon(idx) }
                            </Avatar>
                        </Tooltip>
                    )) }
                </AvatarGroup>
            )
        }
    ];

    return (
        <Card sx={ { mb: 3 } }>
            <CardContent>
                <div style={ { display: 'flex', alignItems: 'center', gap: 8 } }>
                    <Typography variant="h6" gutterBottom sx={ { mr: 1 } }>
                        Notification Preferences
                    </Typography>
                    { isFetching && (
                        <Chip
                            size="small"
                            icon={ <CircularProgress size={ 14 } /> }
                            label="Updating…"
                            variant="outlined"
                        />
                    ) }
                </div>
                <div style={ { height: 400, width: '100%' } }>
                    <DataGrid
                        rows={ preferences || [] }
                        columns={ columns }
                        pagination
                        paginationMode="client"
                        pageSizeOptions={ [ 25, 50, 100 ] }
                        initialState={ {
                            pagination: { paginationModel: { pageSize: 25 } },
                            sorting: { sortModel: [ { field: 'name', sort: 'asc' } ] }
                        } }
                        disableRowSelectionOnClick
                        getRowId={ (row) => row.id }
                        sx={ {
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #e0e0e0'
                            }
                        } }
                    />
                </div>
            </CardContent>
        </Card>
    );
};

export default NotificationPreferencesTable;
